import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Fab,
  alpha,
  Collapse,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

import PropertyCard from 'src/components/PropertyCard';
import PropertyCreateDialog from './PropertyCreateDialog';
import PropertyDetailsDialog from './PropertyDetailsDialog';
import PropertyEditDialog from './PropertyEditDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface City {
  id: string;
  name: string;
  active: boolean;
}

interface Country {
  id: string;
  name: string;
  isoCode: string;
  active: boolean;
}

interface Project {
  id: string;
  name: string;
  number: string;
  street: string;
  active: boolean;
}

interface Property {
  id: string;
  code: string;
  street: string;
  floor: number;
  contact: string;
  countryId: string;
  cityId: string;
  projectId: string;
  propertyTypeId: string;
  propertyType: string;
  city: City;
  country: Country;
  project: Project;
  units: any[];
  active: boolean;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled(Box)({
  width: '100%',
  minHeight: '100vh',
  padding: '8px 16px',
  animation: `${fadeIn} 0.6s ease-out`,
});

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: 12,
  padding: 12,
  borderRadius: 12,
  background: `linear-gradient(135deg, ${alpha('#4CAF50', 0.08)} 0%, ${alpha('#00897B', 0.05)} 100%)`,
  border: `2px solid ${alpha('#4CAF50', 0.2)}`,
  boxShadow: `0 8px 32px ${alpha('#4CAF50', 0.15)}`,
}));

const FilterSection = styled(Box)(({ theme }) => ({
  marginBottom: 12,
  padding: 12,
  borderRadius: 12,
  background: `linear-gradient(135deg, ${alpha('#FFFFFF', 0.98)} 0%, ${alpha('#4CAF50', 0.03)} 100%)`,
  border: `2px solid ${alpha('#4CAF50', 0.15)}`,
  boxShadow: `0 4px 16px ${alpha('#4CAF50', 0.1)}`,
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: 16,
  right: 16,
  background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
  color: '#FFFFFF',
  boxShadow: `0 8px 32px ${alpha('#4CAF50', 0.4)}, 0 0 30px ${alpha('#4CAF50', 0.2)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)',
    transform: 'scale(1.15) rotate(90deg)',
    boxShadow: `0 12px 48px ${alpha('#4CAF50', 0.6)}, 0 0 50px ${alpha('#4CAF50', 0.3)}`,
  },
}));

const PropertiesListing: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterCity, setFilterCity] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPropertyForDetails, setSelectedPropertyForDetails] = useState<Property | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  const dispatch = useAppDispatch();

  // Fetch properties
  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const response = await DataService.get('api/Property/all');

      if (response.ok) {
        const responseData = await response.json();
        let rawProperties: any[] = [];

        if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
          rawProperties = responseData.data;
        } else if (Array.isArray(responseData)) {
          rawProperties = responseData;
        }

        const properties: Property[] = rawProperties.map(property => {
          // Extract project data and transform flat city/country fields to nested objects
          const project = property.project ? {
            id: property.project.id,
            name: property.project.name,
            number: property.project.number,
            street: property.project.street,
            active: property.project.active
          } : null;

          // Extract city from project
          const city = property.project?.cityId ? {
            id: property.project.cityId,
            name: property.project.cityName || '',
            active: true
          } : null;

          // Extract country from project
          const country = property.project?.countryId ? {
            id: property.project.countryId,
            name: property.project.countryName || '',
            isoCode: property.project.countryCode || '',
            active: true
          } : null;

          return {
            id: property.id,
            code: property.code || '',
            street: project?.street || '',
            floor: property.floor || 0,
            contact: property.contact || '',
            countryId: country?.id || '',
            cityId: city?.id || '',
            projectId: project?.id || '',
            propertyTypeId: property.propertyTypeId || '',
            propertyType: property.propertyType || '',
            city: city,
            country: country,
            project: project,
            units: property.units || [],
            active: property.active !== undefined ? property.active : true
          };
        });

        setProperties(properties);
        setFilteredProperties(properties);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      dispatch(
        showAlert({
          message: 'Failed to load properties. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // Apply filters
  useEffect(() => {
    let filtered = [...properties];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        property =>
          property.code.toLowerCase().includes(searchLower) ||
          property.street.toLowerCase().includes(searchLower) ||
          property.contact.toLowerCase().includes(searchLower) ||
          property.propertyType.toLowerCase().includes(searchLower) ||
          property.city?.name.toLowerCase().includes(searchLower) ||
          property.country?.name.toLowerCase().includes(searchLower) ||
          property.project?.name.toLowerCase().includes(searchLower)
      );
    }

    // City filter
    if (filterCity) {
      filtered = filtered.filter(property => property.city?.name === filterCity);
    }

    // Country filter
    if (filterCountry) {
      filtered = filtered.filter(property => property.country?.name === filterCountry);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(property =>
        filterStatus === 'active' ? property.active : !property.active
      );
    }

    // Type filter
    if (filterType) {
      filtered = filtered.filter(property => property.propertyType === filterType);
    }

    setFilteredProperties(filtered);
  }, [searchTerm, filterCity, filterCountry, filterStatus, filterType, properties]);

  // Get unique values for filters
  const uniqueCities = Array.from(new Set(properties.map(p => p.city?.name).filter(Boolean)));
  const uniqueCountries = Array.from(new Set(properties.map(p => p.country?.name).filter(Boolean)));
  const uniqueTypes = Array.from(new Set(properties.map(p => p.propertyType).filter(Boolean)));

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterCity('');
    setFilterCountry('');
    setFilterStatus('all');
    setFilterType('');
  };

  const activeFiltersCount = [filterCity, filterCountry, filterType].filter(Boolean).length +
    (filterStatus !== 'all' ? 1 : 0);

  return (
    <PageContainer>
      <PageHeader>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 900,
            background: 'linear-gradient(135deg, #4CAF50 0%, #00897B 50%, #2196F3 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5,
          }}
        >
          Properties Management
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Manage your property portfolio with style
        </Typography>
      </PageHeader>

      {/* Search and Filter Controls */}
      <FilterSection>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: showFilters ? 1 : 0 }}>
          <TextField
            fullWidth
            placeholder="Search properties by code, address, city, country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#FF6B9D' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                background: alpha('#FFFFFF', 0.8),
                '& fieldset': {
                  borderColor: alpha('#FF6B9D', 0.2),
                  borderWidth: 2,
                },
                '&:hover fieldset': {
                  borderColor: alpha('#FF6B9D', 0.4),
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#FF6B9D',
                },
              }
            }}
          />
          <IconButton
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              background: showFilters
                ? 'linear-gradient(135deg, #FF6B9D 0%, #C44569 100%)'
                : `linear-gradient(135deg, ${alpha('#FF6B9D', 0.15)} 0%, ${alpha('#A770EF', 0.1)} 100%)`,
              color: showFilters ? '#FFFFFF' : '#FF6B9D',
              border: `2px solid ${alpha('#FF6B9D', 0.3)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #FF6B9D 0%, #C44569 100%)',
                color: '#FFFFFF',
                transform: 'scale(1.1) rotate(180deg)',
                boxShadow: `0 4px 12px ${alpha('#FF6B9D', 0.4)}`,
              },
            }}
          >
            <FilterListIcon />
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  height: 20,
                  minWidth: 20,
                  background: 'linear-gradient(135deg, #00D9C0 0%, #00FFF0 100%)',
                  color: '#1A1A2E',
                  fontWeight: 800,
                  fontSize: '0.7rem',
                }}
              />
            )}
          </IconButton>
        </Box>

        <Collapse in={showFilters}>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>City</InputLabel>
                  <Select
                    value={filterCity}
                    label="City"
                    onChange={(e) => setFilterCity(e.target.value)}
                  >
                    <MenuItem value="">All Cities</MenuItem>
                    {uniqueCities.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={filterCountry}
                    label="Country"
                    onChange={(e) => setFilterCountry(e.target.value)}
                  >
                    <MenuItem value="">All Countries</MenuItem>
                    {uniqueCountries.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filterType}
                    label="Type"
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {uniqueTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active Only</MenuItem>
                    <MenuItem value="inactive">Inactive Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {activeFiltersCount > 0 && (
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  onClick={handleClearFilters}
                  size="small"
                  sx={{
                    color: '#FF5E78',
                    '&:hover': {
                      background: alpha('#FF5E78', 0.1),
                    }
                  }}
                >
                  <ClearIcon fontSize="small" />
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    Clear Filters
                  </Typography>
                </IconButton>
              </Box>
            )}
          </Box>
        </Collapse>
      </FilterSection>

      {/* Results Count */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Showing {filteredProperties.length} of {properties.length} properties
        </Typography>
      </Box>

      {/* Properties Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress
            sx={{
              color: '#FF6B9D',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }}
            size={60}
            thickness={4}
          />
        </Box>
      ) : filteredProperties.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            px: 2,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha('#4CAF50', 0.05)} 0%, ${alpha('#00897B', 0.03)} 100%)`,
            border: `2px dashed ${alpha('#4CAF50', 0.2)}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 0.5, fontWeight: 700 }}>
            No properties found
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Try adjusting your search or filters
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {filteredProperties.map((property) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={property.id}>
              <PropertyCard
                property={property}
                onView={() => {
                  setSelectedPropertyForDetails(property);
                  setDetailsDialogOpen(true);
                }}
                onEdit={() => {
                  setSelectedProperty(property);
                  setEditDialogOpen(true);
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Add Button */}
      <StyledFab
        onClick={() => setCreateDialogOpen(true)}
        aria-label="add property"
      >
        <AddIcon sx={{ fontSize: 32 }} />
      </StyledFab>

      {/* Property Create Dialog */}
      <PropertyCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          setCreateDialogOpen(false);
          fetchProperties();
        }}
      />

      {/* Property Details Dialog */}
      <PropertyDetailsDialog
        open={detailsDialogOpen}
        property={selectedPropertyForDetails}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedPropertyForDetails(null);
        }}
        onRefresh={fetchProperties}
      />

      {/* Property Edit Dialog */}
      <PropertyEditDialog
        open={editDialogOpen}
        property={selectedProperty}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedProperty(null);
        }}
        onSuccess={() => {
          setEditDialogOpen(false);
          setSelectedProperty(null);
          fetchProperties();
        }}
      />
    </PageContainer>
  );
};

export default PropertiesListing;
